import React, { useEffect, useMemo, useState } from "react";

const DISMISS_KEY = "aamschool_install_prompt_hidden";
const DISMISS_DURATION_MS = 3 * 24 * 60 * 60 * 1000;

const isIosDevice = () => {
  if (typeof window === "undefined") return false;
  const userAgent = window.navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(userAgent);
};

const isRunningStandalone = () => {
  if (typeof window === "undefined") return false;
  const displayModeStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches;
  const navigatorStandalone = Boolean(window.navigator.standalone);
  return Boolean(displayModeStandalone || navigatorStandalone);
};

function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isRunningStandalone());
  const [isDismissed, setIsDismissed] = useState(false);
  const isiOS = useMemo(() => isIosDevice(), []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const hiddenUntil = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    if (hiddenUntil > Date.now()) {
      setIsDismissed(true);
    } else {
      window.localStorage.removeItem(DISMISS_KEY);
    }

    const mediaQuery = window.matchMedia?.("(display-mode: standalone)");
    const updateInstalledState = () => {
      setIsInstalled(isRunningStandalone());
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
      setIsDismissed(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    mediaQuery?.addEventListener?.("change", updateInstalledState);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      mediaQuery?.removeEventListener?.("change", updateInstalledState);
    };
  }, []);

  const hidePrompt = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DURATION_MS));
    }
    setIsDismissed(true);
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice.catch(() => null);
    setDeferredPrompt(null);

    if (choiceResult?.outcome === "accepted") {
      setIsInstalled(true);
      setIsDismissed(true);
      return;
    }

    hidePrompt();
  };

  if (isInstalled || isDismissed) return null;

  const showIosHelp = isiOS && !isRunningStandalone();
  const showInstallButton = Boolean(deferredPrompt);

  if (!showIosHelp && !showInstallButton) return null;

  return (
    <div className="pwa-install-prompt" role="dialog" aria-label="Install AAMSchool app">
      <button
        type="button"
        className="pwa-install-close"
        onClick={hidePrompt}
        aria-label="Close install prompt"
      >
        x
      </button>
      <p className="pwa-install-kicker">Quick Access</p>
      <h3>Add AAMSchool to your home screen</h3>
      <p className="pwa-install-copy">
        {showInstallButton
          ? "Install the app for faster access and an app-like experience."
          : "On iPhone, tap Share in Safari and then choose Add to Home Screen."}
      </p>
      {showInstallButton ? (
        <button type="button" className="pwa-install-action" onClick={handleInstallClick}>
          Add To Home Screen
        </button>
      ) : null}
      {showIosHelp ? (
        <p className="pwa-install-note">Safari only: Share -> Add to Home Screen</p>
      ) : null}
    </div>
  );
}

export default PwaInstallPrompt;
